import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Euro,
  Search,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronRight,
  Clock,
  Car,
  Filter,
  RefreshCw,
  Save,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminRates = () => {
  const [rates, setRates] = useState([]);
  const [groupedRates, setGroupedRates] = useState([]);
  const [hourlyRates, setHourlyRates] = useState({ car_rate: 60, van_rate: 70 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tripTypeFilter, setTripTypeFilter] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [editingRate, setEditingRate] = useState(null);
  const [editingHourly, setEditingHourly] = useState(false);
  const [tempPrices, setTempPrices] = useState([]);
  const [tempHourly, setTempHourly] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [locations, setLocations] = useState([]);
  const [newRate, setNewRate] = useState({
    pickup_location: "",
    pickup_normalized: "",
    dropoff_location: "",
    dropoff_normalized: "",
    trip_type: "oneway",
    prices: [80, 80, 80, 90, 100, 100, 110, 120],
    is_active: true,
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("adminToken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, []);

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (tripTypeFilter) params.append("trip_type", tripTypeFilter);

      const response = await fetch(
        `${API_URL}/api/admin/rates?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        setRates(data.rates || []);
        setGroupedRates(data.grouped || []);
        
        // Auto-expand first group
        if (data.grouped?.length > 0 && Object.keys(expandedGroups).length === 0) {
          setExpandedGroups({ [data.grouped[0].pickup_normalized]: true });
        }
      } else {
        toast.error("Failed to fetch rates");
      }
    } catch (error) {
      console.error("Error fetching rates:", error);
      toast.error("Error loading rates");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, tripTypeFilter, expandedGroups]);

  const fetchHourlyRates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/hourly-rates`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setHourlyRates(data);
      }
    } catch (error) {
      console.error("Error fetching hourly rates:", error);
    }
  }, [getAuthHeaders]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/locations`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchRates();
    fetchHourlyRates();
    fetchLocations();
  }, [fetchRates, fetchHourlyRates, fetchLocations]);

  const toggleGroup = (pickupNormalized) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [pickupNormalized]: !prev[pickupNormalized],
    }));
  };

  const startEditing = (rate) => {
    setEditingRate(rate.id);
    setTempPrices([...rate.prices]);
  };

  const cancelEditing = () => {
    setEditingRate(null);
    setTempPrices([]);
  };

  const saveRate = async (rateId) => {
    // Validate prices
    for (let i = 0; i < tempPrices.length; i++) {
      if (tempPrices[i] < 0) {
        toast.error(`Price for ${i + 1} passenger(s) must be >= 0`);
        return;
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/rates/${rateId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ prices: tempPrices }),
      });

      if (response.ok) {
        toast.success("Rate updated successfully");
        setEditingRate(null);
        fetchRates();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update rate");
      }
    } catch (error) {
      console.error("Error updating rate:", error);
      toast.error("Error updating rate");
    }
  };

  const toggleRateStatus = async (rateId, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/rates/${rateId}/toggle`, {
        method: "PATCH",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        toast.success(`Rate ${currentStatus ? "disabled" : "enabled"} successfully`);
        fetchRates();
      } else {
        toast.error("Failed to toggle rate status");
      }
    } catch (error) {
      console.error("Error toggling rate:", error);
      toast.error("Error toggling rate status");
    }
  };

  const deleteRate = async (rateId) => {
    if (!window.confirm("Are you sure you want to delete this rate?")) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/rates/${rateId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        toast.success("Rate deleted successfully");
        fetchRates();
      } else {
        toast.error("Failed to delete rate");
      }
    } catch (error) {
      console.error("Error deleting rate:", error);
      toast.error("Error deleting rate");
    }
  };

  const startEditingHourly = () => {
    setEditingHourly(true);
    setTempHourly({ ...hourlyRates });
  };

  const saveHourlyRates = async () => {
    if (tempHourly.car_rate < 1 || tempHourly.van_rate < 1) {
      toast.error("Hourly rates must be at least €1");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/hourly-rates`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          car_rate: parseInt(tempHourly.car_rate),
          van_rate: parseInt(tempHourly.van_rate),
        }),
      });

      if (response.ok) {
        toast.success("Hourly rates updated successfully");
        setEditingHourly(false);
        fetchHourlyRates();
      } else {
        toast.error("Failed to update hourly rates");
      }
    } catch (error) {
      console.error("Error updating hourly rates:", error);
      toast.error("Error updating hourly rates");
    }
  };

  const createRate = async () => {
    // Validate
    if (!newRate.pickup_normalized || !newRate.dropoff_normalized) {
      toast.error("Please select both pickup and dropoff locations");
      return;
    }

    for (let i = 0; i < newRate.prices.length; i++) {
      if (newRate.prices[i] < 0) {
        toast.error(`Price for ${i + 1} passenger(s) must be >= 0`);
        return;
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/rates`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newRate,
          vehicle_type: "Combined",
        }),
      });

      if (response.ok) {
        toast.success("Rate created successfully");
        setShowCreateModal(false);
        setNewRate({
          pickup_location: "",
          pickup_normalized: "",
          dropoff_location: "",
          dropoff_normalized: "",
          trip_type: "oneway",
          prices: [80, 80, 80, 90, 100, 100, 110, 120],
          is_active: true,
        });
        fetchRates();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create rate");
      }
    } catch (error) {
      console.error("Error creating rate:", error);
      toast.error("Error creating rate");
    }
  };

  const handleLocationSelect = (field, location) => {
    if (field === "pickup") {
      setNewRate((prev) => ({
        ...prev,
        pickup_location: location.display,
        pickup_normalized: location.normalized,
      }));
    } else {
      setNewRate((prev) => ({
        ...prev,
        dropoff_location: location.display,
        dropoff_normalized: location.normalized,
      }));
    }
  };

  const filteredGroups = groupedRates.filter((group) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      group.pickup_location.toLowerCase().includes(search) ||
      group.routes.some((r) => r.dropoff_location.toLowerCase().includes(search))
    );
  });

  return (
    <div data-testid="admin-rates">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">
          Rate Management
        </h1>
        <p className="text-slate-600">
          Manage pricing for all routes and services
        </p>
      </div>

      {/* Hourly Rates Card */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-600" />
              Hourly Tour Rates
            </CardTitle>
            {!editingHourly ? (
              <Button
                variant="outline"
                size="sm"
                onClick={startEditingHourly}
                data-testid="edit-hourly-btn"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingHourly(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={saveHourlyRates}
                  data-testid="save-hourly-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">
                  Car (1-3 passengers)
                </span>
              </div>
              {editingHourly ? (
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">€</span>
                  <Input
                    type="number"
                    value={tempHourly.car_rate}
                    onChange={(e) =>
                      setTempHourly((prev) => ({
                        ...prev,
                        car_rate: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-24"
                    min="1"
                    data-testid="hourly-car-input"
                  />
                  <span className="text-slate-500">/hour</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-primary-600">
                  €{hourlyRates.car_rate}/hr
                </p>
              )}
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">
                  Van (4-8 passengers)
                </span>
              </div>
              {editingHourly ? (
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">€</span>
                  <Input
                    type="number"
                    value={tempHourly.van_rate}
                    onChange={(e) =>
                      setTempHourly((prev) => ({
                        ...prev,
                        van_rate: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-24"
                    min="1"
                    data-testid="hourly-van-input"
                  />
                  <span className="text-slate-500">/hour</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-primary-600">
                  €{hourlyRates.van_rate}/hr
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transfer Rates Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="font-heading flex items-center gap-2">
              <Euro className="w-5 h-5 text-primary-600" />
              Transfer Rates ({rates.length} routes)
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRates}
                data-testid="refresh-rates-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateModal(true)}
                data-testid="create-rate-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Route
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-rates-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={tripTypeFilter}
                onChange={(e) => setTripTypeFilter(e.target.value)}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="trip-type-filter"
              >
                <option value="">All Trip Types</option>
                <option value="oneway">One-Way</option>
                <option value="return">Round Trip</option>
              </select>
            </div>
          </div>

          {/* Rates List */}
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              Loading rates...
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No rates found
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGroups.map((group) => (
                <div
                  key={group.pickup_normalized}
                  className="border border-slate-200 rounded-lg overflow-hidden"
                >
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(group.pickup_normalized)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`group-${group.pickup_normalized}`}
                  >
                    <div className="flex items-center gap-3">
                      {expandedGroups[group.pickup_normalized] ? (
                        <ChevronDown className="w-5 h-5 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      )}
                      <span className="font-semibold text-slate-900">
                        {group.pickup_location}
                      </span>
                      <span className="text-sm text-slate-500">
                        ({group.routes.length} destinations)
                      </span>
                    </div>
                  </button>

                  {/* Group Content */}
                  <AnimatePresence>
                    {expandedGroups[group.pickup_normalized] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="divide-y divide-slate-100">
                          {group.routes.map((rate) => (
                            <div
                              key={rate.id}
                              className={`p-4 ${
                                !rate.is_active ? "bg-slate-50 opacity-60" : ""
                              }`}
                              data-testid={`rate-row-${rate.id}`}
                            >
                              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                {/* Route Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-slate-900 truncate">
                                      → {rate.dropoff_location}
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        rate.trip_type === "return"
                                          ? "bg-blue-100 text-blue-700"
                                          : "bg-green-100 text-green-700"
                                      }`}
                                    >
                                      {rate.trip_type === "return"
                                        ? "Round Trip"
                                        : "One-Way"}
                                    </span>
                                    {!rate.is_active && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                        Disabled
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Prices */}
                                <div className="flex-1">
                                  {editingRate === rate.id ? (
                                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-1">
                                      {tempPrices.map((price, idx) => (
                                        <div key={idx} className="text-center">
                                          <label className="text-xs text-slate-500 block">
                                            {idx + 1}p
                                          </label>
                                          <Input
                                            type="number"
                                            value={price}
                                            onChange={(e) => {
                                              const newPrices = [...tempPrices];
                                              newPrices[idx] =
                                                parseInt(e.target.value) || 0;
                                              setTempPrices(newPrices);
                                            }}
                                            className="w-full text-center text-sm px-1"
                                            min="0"
                                            data-testid={`price-input-${idx}`}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 text-center">
                                      {rate.prices.map((price, idx) => (
                                        <div key={idx}>
                                          <span className="text-xs text-slate-500 block">
                                            {idx + 1}p
                                          </span>
                                          <span
                                            className={`text-sm font-medium ${
                                              price === 0
                                                ? "text-red-500"
                                                : "text-slate-900"
                                            }`}
                                          >
                                            €{price}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2 justify-end">
                                  {editingRate === rate.id ? (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={cancelEditing}
                                        data-testid="cancel-edit-btn"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => saveRate(rate.id)}
                                        data-testid="save-rate-btn"
                                      >
                                        <Save className="w-4 h-4 mr-1" />
                                        Save
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => startEditing(rate)}
                                        data-testid={`edit-rate-${rate.id}`}
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          toggleRateStatus(rate.id, rate.is_active)
                                        }
                                        data-testid={`toggle-rate-${rate.id}`}
                                      >
                                        {rate.is_active ? (
                                          <ToggleRight className="w-4 h-4 text-green-600" />
                                        ) : (
                                          <ToggleLeft className="w-4 h-4 text-slate-400" />
                                        )}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => deleteRate(rate.id)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`delete-rate-${rate.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Rate Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200">
                <h2 className="font-heading font-bold text-xl text-slate-900">
                  Add New Route
                </h2>
                <p className="text-sm text-slate-600">
                  Create a new pricing entry for a route
                </p>
              </div>

              <div className="p-6 space-y-4">
                {/* Trip Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Trip Type
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        value="oneway"
                        checked={newRate.trip_type === "oneway"}
                        onChange={(e) =>
                          setNewRate((prev) => ({
                            ...prev,
                            trip_type: e.target.value,
                          }))
                        }
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">One-Way</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        value="return"
                        checked={newRate.trip_type === "return"}
                        onChange={(e) =>
                          setNewRate((prev) => ({
                            ...prev,
                            trip_type: e.target.value,
                          }))
                        }
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">Round Trip</span>
                    </label>
                  </div>
                </div>

                {/* Pickup Location */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Pickup Location
                  </label>
                  <select
                    value={newRate.pickup_normalized}
                    onChange={(e) => {
                      const loc = locations.find(
                        (l) => l.normalized === e.target.value
                      );
                      if (loc) handleLocationSelect("pickup", loc);
                    }}
                    className="w-full border border-slate-200 rounded-md px-3 py-2"
                    data-testid="new-rate-pickup"
                  >
                    <option value="">Select pickup location...</option>
                    {locations.map((loc) => (
                      <option key={loc.normalized} value={loc.normalized}>
                        {loc.display}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dropoff Location */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Dropoff Location
                  </label>
                  <select
                    value={newRate.dropoff_normalized}
                    onChange={(e) => {
                      const loc = locations.find(
                        (l) => l.normalized === e.target.value
                      );
                      if (loc) handleLocationSelect("dropoff", loc);
                    }}
                    className="w-full border border-slate-200 rounded-md px-3 py-2"
                    data-testid="new-rate-dropoff"
                  >
                    <option value="">Select dropoff location...</option>
                    {locations.map((loc) => (
                      <option key={loc.normalized} value={loc.normalized}>
                        {loc.display}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Prices */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Prices by Passenger Count (€)
                  </label>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                    {newRate.prices.map((price, idx) => (
                      <div key={idx}>
                        <label className="text-xs text-slate-500 block text-center">
                          {idx + 1}p
                        </label>
                        <Input
                          type="number"
                          value={price}
                          onChange={(e) => {
                            const newPrices = [...newRate.prices];
                            newPrices[idx] = parseInt(e.target.value) || 0;
                            setNewRate((prev) => ({
                              ...prev,
                              prices: newPrices,
                            }));
                          }}
                          className="text-center"
                          min="0"
                          data-testid={`new-rate-price-${idx}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Prices 1-3p are for Car, 4-8p are for Van. Use 0 to indicate
                    unavailable.
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button onClick={createRate} data-testid="submit-new-rate">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Rate
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminRates;
